import { faEye, faPen, faTimeline } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Space, Switch } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import { Link } from "react-router-dom";
import { DEFAULT_COLUMN_CONFIGS } from "../../../constants/default-customize";
import { ISupplierResult } from "../../../interfaces/supplier";
import ContentCellNowrap from "../../../components/table/content-cell-nowrap";
import ContentCellBool from "../../../components/table/content-cell-bool";

export const getSupplierColumns = (
  hasPermissionEditor: boolean,
  columnConfigs = DEFAULT_COLUMN_CONFIGS,
  onOpenChangeStatus?: (supplier: ISupplierResult) => void
): ColumnsType<ISupplierResult> => {
  const allColumns: ColumnsType<ISupplierResult> = [
    {
      title: "Supplier Code",
      dataIndex: "supplierCode",
      key: "supplierCode",
      width: 150,
      fixed: "left",
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Supplier Name",
      dataIndex: "supplierName",
      key: "supplierName",
      width: 300,
      fixed: "left",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      render: (value: string) => <ContentCellNowrap>{value}</ContentCellNowrap>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 350,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      render: (value: string) => <ContentCellNowrap>{value}</ContentCellNowrap>,
    },
    {
      title: "Email 1",
      dataIndex: "email1",
      key: "email1",
      width: 350,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Credit Supplier",
      dataIndex: "isCreditSupplier",
      key: "isCreditSupplier",
      width: 150,
      render: (value: boolean) => <ContentCellBool value={value} />,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      align: "center",
    },
    {
      title: "Alternative Supplier Name",
      dataIndex: "supplierAlternativeName",
      key: "supplierAlternativeName",
      width: 300,
      sorter: false,
      render: (_: any, record: ISupplierResult) => {
        let names: string[] = record?.alternativeNames ?? [];
        return <ContentCellNowrap>{names.join(", ")}</ContentCellNowrap>;
      },
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: 150,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "RFI-21 Form Required",
      dataIndex: "isRfi21FormTequired",
      key: "isRfi21FormTequired",
      width: 200,
      render: (value: boolean) => <ContentCellBool value={value} />,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      align: "center",
    },
    {
      title: "Part of a Group",
      dataIndex: "isPartOfGroup",
      key: "isPartOfGroup",
      width: 150,
      render: (value: boolean) => <ContentCellBool value={value} />,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      align: "center",
    },
    {
      title: "Group Name",
      dataIndex: "groupName",
      key: "groupName",
      width: 250,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
      width: 300,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      width: 150,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Email 2",
      dataIndex: "email2",
      key: "email2",
      width: 350,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Invoice sender email",
      dataIndex: "email3",
      key: "email3",
      width: 350,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Product Head",
      dataIndex: "productHead",
      key: "productHead",
      width: 300,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Product Owner",
      dataIndex: "productOwner",
      key: "productOwner",
      width: 300,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "AP Owner",
      dataIndex: "apOwner",
      key: "apOwner",
      width: 300,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "AP Escalation",
      dataIndex: "apEscalation",
      key: "apEscalation",
      width: 300,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 180,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 100,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      align: "center",
    },
    {
      title: "Credit Limit",
      dataIndex: "isCreditLimit",
      key: "isCreditLimit",
      width: 120,
      render: (_: boolean, record) => {
        if (!record.isCreditSupplier) return null;

        if (record.isCreditLimit) {
          const v = record.creditLimitValue;
          return <ContentCellNowrap>{v}</ContentCellNowrap>;
        }

        return <ContentCellNowrap>Unlimited</ContentCellNowrap>;
      },
      sorter: true,
      sortDirections: ["ascend", "descend"],
      align: "center",
    },
    {
      title: "Term Type",
      dataIndex: "termType",
      key: "termType",
      width: 120,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "No. of Installments",
      dataIndex: "noOfInstallments",
      key: "noOfInstallments",
      width: 180,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      align: "center",
    },
    {
      title: "Nominal Account Debit",
      dataIndex: "nominalAccountDebit",
      key: "nominalAccountDebit",
      width: 250,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Nominal Account Credit",
      dataIndex: "nominalAccountCredit",
      key: "nominalAccountCredit",
      width: 250,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Unders & Overs Auto-Match Nominal Code",
      dataIndex: "undersOversAutoMatchNominalCode",
      key: "undersOversAutoMatchNominalCode",
      width: 350,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Actions",
      key: "action",
      width: 80,
      fixed: "right",
      align: "center",
      render: (_: any, record: ISupplierResult) => (
        <Space size="small" className="box-action" align="center">
          {hasPermissionEditor ? (
            <>
              <Link to={`/supplier/${record.id}`}>
                <Button type="link" size="small">
                  <FontAwesomeIcon icon={faPen} />
                </Button>
              </Link>
              <div>|</div>
              <Link to={`/supplier/${record.id}/audit-trail`}>
                <Button type="link" size="small">
                  <FontAwesomeIcon icon={faTimeline} />
                </Button>
              </Link>
            </>
          ) : (
            <>
              <Link to={`/supplier/${record.id}`}>
                <Button type="link" size="small">
                  <FontAwesomeIcon icon={faEye} />
                </Button>
              </Link>
              <div>|</div>
              <Link to={`/supplier/${record.id}/audit-trail`}>
                <Button type="link" size="small">
                  <FontAwesomeIcon icon={faTimeline} />
                </Button>
              </Link>
            </>
          )}
        </Space>
      ),
    },
    {
      title: "State",
      key: "state",
      dataIndex: "isActive",
      width: 60,
      fixed: "right",
      align: "center",
      render: (_: any, record: ISupplierResult) => (
        <Switch
          disabled={!hasPermissionEditor}
          checked={record.isActive}
          size="small"
          onClick={() => onOpenChangeStatus?.(record)}
        />
      ),
    },
  ];

  const visibleColumnKeys = columnConfigs
    .filter((c) => c.isDisplay)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((c) => c.column);

  const mainColumns = visibleColumnKeys
    .map((key) =>
      allColumns.find((col): col is ColumnType<ISupplierResult> => "dataIndex" in col && col.dataIndex === key)
    )
    .filter((col): col is ColumnType<ISupplierResult> => col !== undefined);

  const actionColumn = allColumns.find((col) => col.key === "action");
  const stateColumn = allColumns.find((col) => col.key === "state");

  const resultColumns = [...mainColumns];

  if (actionColumn) {
    resultColumns.push(actionColumn);
  }
  if (stateColumn) {
    resultColumns.push(stateColumn);
  }

  return resultColumns;
};
